import { useState, type SyntheticEvent } from "react";

import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { FieldError } from "../components/ui/field";
import { TextField } from "./billing-fields";
import { dayToSeconds, endOfDay } from "./billing-format";
import {
  BillingFormError,
  fieldState,
  REASON_REQUIREMENT,
  useFormErrors,
  type FieldErrors,
  type FieldGuide,
  type FormErrors,
} from "./billing-validation";

/**
 * The small step a quote or its terms take next (ADR-0075, ADR-0077): send, approve, end, or
 * cancel, each with the reason History will show. One dialog, one form, five verbs.
 */

export type AgreementAction =
  | { kind: "quote"; quoteId: string; action: "send" | "cancel" }
  | { kind: "terms"; termsId: string; action: "approve" | "end" | "cancel" };

export type AgreementActionInput = {
  reason: string;
  validUntil: number | null;
  endsAt: number | null;
};

const GUIDE: FieldGuide = {
  requirements: {
    reason: REASON_REQUIREMENT,
    validUntil: "A date, today or later. The customer can accept until the end of this day.",
    ends: "Optional. A date, today or later; blank means today.",
  },
};

function actionTitle(action: AgreementAction): string {
  if (action.kind === "quote") return action.action === "send" ? "Send quote" : "Cancel quote";
  return { approve: "Approve terms", end: "End terms", cancel: "Cancel terms" }[action.action];
}

function submitLabel(action: AgreementAction): string {
  if (action.kind === "quote" && action.action === "send") return "Send to the customer";
  return actionTitle(action);
}

function transitionErrors(
  action: AgreementAction,
  reason: string,
  day: string,
  ends: string,
): FieldErrors {
  const errors: FieldErrors = {};
  const length = reason.trim().length;
  if (length < 3) errors["reason"] = length === 0 ? "Required." : "At least 3 characters.";
  else if (length > 500) errors["reason"] = "At most 500 characters.";
  if (action.kind === "quote" && action.action === "send") {
    const validUntil = endOfDay(day);
    if (validUntil === null) errors["validUntil"] = "Enter the last day the customer can accept.";
    else if (validUntil < Math.floor(Date.now() / 1_000)) {
      errors["validUntil"] = "The last day to accept cannot be in the past.";
    }
  }
  if (action.kind === "terms" && action.action === "end") {
    if (ends.trim() !== "" && dayToSeconds(ends) === null) {
      errors["ends"] = "The end date is not a date.";
    }
  }
  return errors;
}

function DayField({
  name,
  id,
  label,
  required,
  value,
  errors,
  onChange,
}: {
  name: "validUntil" | "ends";
  id: string;
  label: string;
  required: boolean;
  value: string;
  errors: FormErrors;
  onChange: (value: string) => void;
}) {
  return (
    <TextField
      {...fieldState(errors, GUIDE.requirements, name, id)}
      label={label}
      type="date"
      value={value}
      required={required}
      onChange={(next) => {
        errors.clear(name);
        onChange(next);
      }}
    />
  );
}

/** The one to three fields an action asks for, and the only part of the dialog that scrolls. */
function ActionFields({
  sending,
  ending,
  reason,
  reasonId,
  day,
  ends,
  errors,
  onReason,
  onDay,
  onEnds,
}: {
  sending: boolean;
  ending: boolean;
  reason: string;
  reasonId: string;
  day: string;
  ends: string;
  errors: FormErrors;
  onReason: (value: string) => void;
  onDay: (value: string) => void;
  onEnds: (value: string) => void;
}) {
  return (
    <div className="space-y-3">
      {sending ? (
        <DayField
          name="validUntil"
          id="quote-valid-until"
          label="Customer can accept until"
          required
          value={day}
          errors={errors}
          onChange={onDay}
        />
      ) : null}
      <TextField
        {...fieldState(errors, GUIDE.requirements, "reason", reasonId)}
        label="Reason"
        value={reason}
        required
        maxLength={500}
        onChange={(value) => {
          errors.clear("reason");
          onReason(value);
        }}
      />
      {ending ? (
        <DayField
          name="ends"
          id="terms-end-date"
          label="Ends on (leave blank for today)"
          required={false}
          value={ends}
          errors={errors}
          onChange={onEnds}
        />
      ) : null}
    </div>
  );
}

function ActionButtons({
  action,
  pending,
  onClose,
}: {
  action: AgreementAction;
  pending: boolean;
  onClose: () => void;
}) {
  const destructive = action.action === "cancel" || action.action === "end";
  return (
    <DialogFooter>
      <Button type="button" variant="ghost" onClick={onClose}>
        Keep as is
      </Button>
      <Button type="submit" variant={destructive ? "destructive" : "default"} disabled={pending}>
        {submitLabel(action)}
      </Button>
    </DialogFooter>
  );
}

function ActionForm({
  action,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  action: AgreementAction;
  pending: boolean;
  error: string | null;
  onSubmit: (input: AgreementActionInput) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [day, setDay] = useState("");
  const [ends, setEnds] = useState("");
  const errors = useFormErrors(GUIDE);
  const sending = action.kind === "quote" && action.action === "send";
  const ending = action.kind === "terms" && action.action === "end";
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const wrong = transitionErrors(action, reason, day, ends);
    if (Object.keys(wrong).length > 0) {
      errors.fail(new BillingFormError(wrong));
      return;
    }
    errors.reset();
    onSubmit({
      reason: reason.trim(),
      validUntil: sending ? endOfDay(day) : null,
      endsAt: ending && ends.trim() !== "" ? dayToSeconds(ends) : null,
    });
  }
  const summary = errors.summary ?? error;
  const reasonId = `${action.kind}-${action.action}-reason`;
  return (
    <DialogForm onSubmit={submit} aria-label={actionTitle(action)} noValidate>
      <DialogBody>
        <ActionFields
          sending={sending}
          ending={ending}
          reason={reason}
          reasonId={reasonId}
          day={day}
          ends={ends}
          errors={errors}
          onReason={setReason}
          onDay={setDay}
          onEnds={setEnds}
        />
        {summary ? <FieldError>{summary}</FieldError> : null}
      </DialogBody>
      <ActionButtons action={action} pending={pending} onClose={onClose} />
    </DialogForm>
  );
}

function actionKey(action: AgreementAction): string {
  return action.kind === "quote"
    ? `quote-${action.quoteId}-${action.action}`
    : `terms-${action.termsId}-${action.action}`;
}

export function BillingAgreementActionDialog({
  action,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  action: AgreementAction | null;
  pending: boolean;
  error: string | null;
  onSubmit: (input: AgreementActionInput) => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={action !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        {action ? (
          <>
            <DialogHeader>
              <DialogTitle>{actionTitle(action)}</DialogTitle>
            </DialogHeader>
            <ActionForm
              key={actionKey(action)}
              action={action}
              pending={pending}
              error={error}
              onSubmit={onSubmit}
              onClose={onClose}
            />
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
